/* eslint-disable max-lines */
import moment from 'moment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import './fonts/fonts';
import {
  renderAllergies,
  renderCamera,
  renderCheckboxes,
  renderCurrentMedications,
  renderDatePicker,
  renderDropdown,
  renderFamilyHistory,
  renderHPI,
  renderHeader,
  renderHyperLink,
  renderImage,
  renderLabel,
  renderLineBreak,
  renderOrderAndResult,
  renderOtherProblems,
  renderParagraph,
  renderPastMedicalHistory,
  renderRadioButtons,
  renderRange,
  renderRating,
  renderReviewOfSystems,
  renderSignature,
  renderSocialHistory,
  renderStaticImage,
  renderSurgicalHistory,
  renderTags,
  renderTextArea,
  renderTextInput,
  renderVaccines
} from './lib';

pdfMake.vfs = pdfFonts;

// Helper function to wrap content in a borderless table with horizontal margins
const wrapInTable = (content) => {
  if (!content) return null;

  return {
    table: {
      widths: ['*'],
      body: [
        [
          {
            ...content,
            border: [false, false, false, false]
          }
        ]
      ]
    },
    layout: 'noBorders',
    margin: [35, 0, 35, 0],
    unbreakable: true
  };
};

// Helper function to process individual elements
const processElement = async (element, safeJsonData, formJson) => {
  if (!element) return null;

  let content = null;

  if (element.static) {
    switch (element.element) {
      case 'Header':
        content = renderHeader(element);
        break;
      case 'LineBreak':
        content = renderLineBreak();
        break;
      case 'Label':
        content = renderLabel(element);
        break;
      case 'Paragraph':
        content = renderParagraph(element);
        break;
      case 'Image':
        content = await renderStaticImage(element);
        break;
      case 'HyperLink':
        content = renderHyperLink(element);
        break;
      default:
        break;
    }
  } else if (element.field_name) {
    if (element.element === 'Image' && element.src) {
      content = await renderStaticImage(element);
    } else {
      const dataItem = safeJsonData.find((item) => item && item.name === element.field_name);

      if (dataItem) {
        const prefix = dataItem.name.split('_')[0];

        let parsed = null;
        const structuredDataPrefixes = [
          'family',
          'social',
          'surgical',
          'review',
          'allergies',
          'vaccines',
          'medications',
          'currentMedications',
          'problems',
          'otherProblems',
          'hpi',
          'orders',
          'orderAndResult',
          'past',
          'pastMedicalHistory'
        ];

        if (structuredDataPrefixes.includes(prefix)) {
          try {
            if (!dataItem.value || dataItem.value === '' || dataItem.value === 'null') {
              parsed = null;
            } else {
              parsed = JSON.parse(dataItem.value);
            }
          } catch (e) {
            parsed = null;
          }
        }

        // Handle different field types with data
        switch (prefix) {
          case 'family':
            content = parsed
              ? renderFamilyHistory(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'social':
            content = parsed
              ? renderSocialHistory(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'surgical':
            content = parsed
              ? renderSurgicalHistory(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'review':
            content = parsed
              ? renderReviewOfSystems(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'allergies':
            content = parsed
              ? renderAllergies(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'vaccines':
            content = parsed
              ? renderVaccines(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'medications':
          case 'currentMedications':
            content = parsed
              ? renderCurrentMedications(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'problems':
          case 'otherProblems':
            content = parsed
              ? renderOtherProblems(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'hpi':
            content = parsed
              ? renderHPI(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'orders':
          case 'orderAndResult':
            content = parsed
              ? renderOrderAndResult(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          case 'past':
          case 'pastMedicalHistory':
            content = parsed
              ? renderPastMedicalHistory(parsed, element.text)
              : renderTextInput(formJson, dataItem.value, dataItem.name);
            break;
          default:
            switch (element.element) {
              case 'RadioButtons':
                content = renderRadioButtons(formJson, dataItem.value, dataItem.name);
                break;
              case 'Checkboxes':
                content = renderCheckboxes(formJson, dataItem.value, dataItem.name);
                break;
              case 'Signature':
                content = renderSignature(formJson, dataItem.value, dataItem.name);
                break;
              case 'Camera':
                content = renderCamera(formJson, dataItem.value, dataItem.name);
                break;
              case 'Image':
                content = renderImage(dataItem.value);
                break;
              case 'TextArea':
                content = renderTextArea(formJson, dataItem.value, dataItem.name);
                break;
              case 'Dropdown':
                content = renderDropdown(formJson, dataItem.value, dataItem.name);
                break;
              case 'DatePicker':
                content = renderDatePicker(formJson, dataItem.value, dataItem.name);
                break;
              case 'Range':
                content = renderRange(formJson, dataItem.value, dataItem.name);
                break;
              case 'Rating':
                content = renderRating(formJson, dataItem.value, dataItem.name);
                break;
              default:
                content = renderTextInput(formJson, dataItem.value, dataItem.name);
                break;
            }
            break;
        }
      } else {
        // Handle missing data
        switch (element.element) {
          case 'RadioButtons':
            content = renderRadioButtons(formJson, null, element.field_name);
            break;
          case 'Checkboxes':
            content = renderCheckboxes(formJson, null, element.field_name);
            break;
          case 'Signature':
            content = renderSignature(formJson, null, element.field_name);
            break;
          case 'Camera':
            content = renderCamera(formJson, null, element.field_name);
            break;
          case 'TextArea':
            content = renderTextArea(formJson, null, element.field_name);
            break;
          case 'Dropdown':
            content = renderDropdown(formJson, null, element.field_name);
            break;
          case 'DatePicker':
            content = renderDatePicker(formJson, null, element.field_name);
            break;
          case 'Range':
            content = renderRange(formJson, null, element.field_name);
            break;
          case 'Rating':
            content = renderRating(formJson, null, element.field_name);
            break;
          case 'Tags':
            content = renderTags(formJson, null, element.field_name);
            break;
          default:
            content = renderTextInput(formJson, null, element.field_name);
            break;
        }
      }
    }
  }

  return content;
};

const generateBodyContent = async (jsonData, formJson) => {
  const bodyContent = [];

  // Handle empty or invalid data
  if (!formJson || !Array.isArray(formJson) || formJson.length === 0) {
    bodyContent.push({
      text: 'No form structure available',
      style: 'placeholder',
      margin: [0, 20, 0, 0]
    });
    return [{ stack: bodyContent, margin: [15, 15, 15, 15] }];
  }

  // Ensure jsonData is an array
  const safeJsonData = Array.isArray(jsonData) ? jsonData : [];

  // Build element lookup map by ID
  const elementMap = {};
  formJson.forEach((element) => {
    if (element && element.id) {
      elementMap[element.id] = element;
    }
  });

  // Track processed child elements to avoid duplicates
  const processedChildIds = new Set();

  for (const [, element] of formJson.entries()) {
    // Skip if element is null or undefined
    if (!element) continue;

    // Skip if this element is a child that has already been processed
    if (processedChildIds.has(element.id)) continue;

    let content = null;

    // Handle column layout elements - display children sequentially instead of in columns
    if (
      element.element === 'TwoColumnRow' ||
      element.element === 'ThreeColumnRow' ||
      element.element === 'FourColumnRow'
    ) {
      if (
        element.childItems &&
        Array.isArray(element.childItems) &&
        element.childItems.length > 0
      ) {
        // Process child elements sequentially
        const childContents = [];
        for (const childId of element.childItems) {
          if (childId && elementMap[childId]) {
            const childElement = elementMap[childId];
            const childContent = await processElement(childElement, safeJsonData, formJson);
            if (childContent) {
              if (Array.isArray(childContent)) {
                childContents.push(...childContent);
              } else {
                childContents.push(childContent);
              }
            }
            // Mark this child as processed
            processedChildIds.add(childId);
          }
        }

        // Create sequential layout
        if (childContents.length > 0) {
          content = wrapInTable({
            stack: childContents,
            margin: [0, 0, 0, 5]
          });
        }
      }
    } else {
      // Use processElement helper for all other elements to ensure consistent processing
      const elementContent = await processElement(element, safeJsonData, formJson);
      if (elementContent) {
        if (Array.isArray(elementContent)) {
          content = wrapInTable({ stack: elementContent });
        } else {
          content = wrapInTable(elementContent);
        }
      } else {
        content = null;
      }
    }

    if (content) {
      bodyContent.push(content);
    }
  }

  return [
    {
      stack: bodyContent,
      margin: [0, 0, 0, 0]
    }
  ];
};

const handleGenerate = async (jsonData, formJson, formName) => {
  const bodyContent = await generateBodyContent(jsonData, formJson);

  const headerTable = {
    table: {
      widths: ['*'],
      body: [
        [
          {
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    text: [
                      { text: formName + '\n', fontSize: 18, bold: true },
                      { text: 'Form Response  ' + moment().format('MM/DD/YYYY'), fontSize: 10 }
                    ],
                    color: 'white',
                    fillColor: '#0D3B50',
                    margin: [25, 20, 25, 20],
                    border: [false, false, false, false]
                  }
                ]
              ]
            },
            layout: 'noBorders',
            margin: [20, 0, 20, 0],
            border: [false, false, false, false]
          }
        ]
      ]
    },
    layout: 'noBorders',
    margin: [0, 0, 0, 10]
  };

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [0, 20, 0, 20],

    defaultStyle: {
      font: 'PlusJakartaSans',
      fontSize: 12
    },

    styles: {
      sectionHeader: {
        fontSize: 13,
        bold: true,
        color: '#0D3B50'
      },
      subHeader: {
        fontSize: 12,
        bold: true,
        color: '#0D3B50'
      },
      summaryHeader: {
        fontSize: 6,
        bold: false,
        color: '#666666',
        characterSpacing: 1.5
      },
      tableHeader: {
        fontSize: 9,
        bold: false,
        color: '#666666'
      },
      tableCell: {
        fontSize: 9,
        bold: true,
        color: '#003A4B'
      },
      narrative: {
        fontSize: 10,
        lineHeight: 1.2
      },
      header: {
        fontSize: 14,
        bold: true
      },
      label: {
        fontSize: 11,
        bold: true
      },
      paragraph: {
        fontSize: 11,
        lineHeight: 1.3
      },
      fieldType: {
        fontSize: 9,
        color: '#666666',
        margin: [0, 2, 0, 0]
      },
      fieldLabel: {
        fontSize: 11,
        bold: true
      },
      fieldValue: {
        fontSize: 10
      },
      placeholder: {
        fontSize: 10,
        color: '#888888'
      },
      optionText: {
        fontSize: 10,
        color: '#333333'
      }
    },

    content: [headerTable, ...bodyContent]
  };

  return new Promise((resolve, _reject) => {
    const pdfDocGenerator = pdfMake.createPdf(docDefinition);
    pdfDocGenerator.getBlob((blob) => {
      resolve(blob);
    });
  });
};

export default async function generatePdfForm({ json, form_json, name }) {
  let data = [];

  // Handle empty or missing json
  if (!json) {
    console.warn('No JSON data provided for PDF generation');
    data = [];
  } else {
    try {
      // Handle json.fields being a string or already parsed
      if (typeof json.fields === 'string') {
        data = JSON.parse(json.fields);
      } else if (Array.isArray(json.fields)) {
        data = json.fields;
      } else if (json.fields) {
        // If json.fields exists but is not an array or string, wrap it
        data = [json.fields];
      } else {
        data = [];
      }
    } catch (error) {
      console.error('Error parsing JSON fields:', error);
      data = [];
    }
  }

  // Ensure data is an array
  if (!Array.isArray(data)) {
    data = [];
  }

  // Handle empty or missing form_json
  const formFields = form_json?.fields || [];

  return handleGenerate(data, formFields, name || 'Form');
}
